import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { LogIn, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { brand } from '../config/site';

const AdminLogin = () => {
  const { login, isAuthed } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (isAuthed) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast({ title: 'Welcome back', description: 'Signed in successfully.' });
      nav('/admin');
    } catch (e) {
      toast({ title: 'Login failed', description: e?.response?.data?.detail || 'Please check your credentials.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(148,179,61,0.14), transparent 55%), radial-gradient(ellipse at 70% 80%, rgba(229,0,109,0.12), transparent 55%), linear-gradient(#06080d, #06080d)',
        }}
      />
      <div aria-hidden className="absolute inset-0 -z-10 bg-grid mask-radial" />

      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.02] p-8 backdrop-blur-md"
      >
        <div className="flex flex-col items-center text-center">
          <img src={brand.logoUrl} alt={brand.name} className="h-10 w-auto" />
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[11px] uppercase tracking-widest text-brand-green-light">
            <ShieldCheck className="h-3.5 w-3.5" /> Admin
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-white">Sign in to manage your site</h1>
          <p className="mt-1 text-sm text-slate-400">Access is restricted to authorized administrators.</p>
        </div>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="info@recreasol.com"
              className="mt-2 bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
              autoFocus
              required
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 bg-black/30 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-brand-green/60"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={busy}
          className="mt-8 w-full h-11 bg-brand-green hover:bg-brand-green-light text-black font-medium btn-transition flex items-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>

        <p className="mt-6 text-center text-xs text-slate-500">
          Forgot password? Contact your site administrator.
        </p>
      </form>
    </div>
  );
};

export default AdminLogin;
